import { DndContext, DragEndEvent, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css } from '@emotion/react';
import { ScanTemplatePart } from '@kaiizen/kai-scan-template-system';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../Styles';

const LINE_ITEM_LIST_STYLE = css`
  label: LINE_ITEM_LIST_STYLE;
  display: flex;
  flex-direction: column;
`;

const LINE_ITEM_ITEM_STYLE = css`
  label: LINE_ITEM_ITEM_STYLE;
  ${TYPOGRAPHY2.small};
  min-height: ${LAYOUT2.spacing1 * 3.5}px;
  display: flex;
  align-items: center;
  color: var(--textGreen);
  gap: ${LAYOUT2.spacing1}px;
  padding: ${LAYOUT2.spacing1}px 0;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--hoverBackground);
  }

  &:active {
    cursor: grabbing;
  }
`;

const LINE_ITEM_ITEM_DRAGGING_STYLE = css`
  label: LINE_ITEM_ITEM_DRAGGING_STYLE;
  opacity: 0.5;
`;

const LINE_ITEM_COUNT_STYLE = css`
  label: LINE_ITEM_COUNT_STYLE;
  ${TYPOGRAPHY2.small};
  color: var(--text4);
  width: ${LAYOUT2.spacing1p5}px;
  flex-shrink: 0;
`;

const LINE_ITEM_CONTENT_WRAPPER_STYLE = css`
  label: LINE_ITEM_CONTENT_WRAPPER_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1 * .5}px;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  img {
    flex-shrink: 0;
  }

  /* Wrap any bare text nodes in an ellipsis-capable container */
  & > * {
    min-width: 0;
  }
`;

const LINE_ITEM_TEXT_WRAPPER_STYLE = css`
  label: LINE_ITEM_TEXT_WRAPPER_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1 * .5}px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  img {
    flex-shrink: 0;
  }
`;

const DRAG_HANDLE_STYLE = css`
  label: DRAG_HANDLE_STYLE;
  display: flex;
  align-items: center;
  color: ${COLORS2.alwaysDark.text.darkGray};
  flex-shrink: 0;
`;

const DRAG_OVERLAY_ITEM_STYLE = css`
  label: DRAG_OVERLAY_ITEM_STYLE;
  ${TYPOGRAPHY2.small};
  min-height: ${LAYOUT2.spacing1 * 3.5}px;
  display: flex;
  align-items: center;
  color: var(--textGreen);
  gap: ${LAYOUT2.spacing1}px;
  padding: ${LAYOUT2.spacing1}px 0;
  cursor: grabbing;
  background-color: var(--background);
  border: 1px solid ${COLORS2.alwaysDark.border.darkGray};
  width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const ScanTemplatePartListItem = <T extends ScanTemplatePart>({
  scanTemplatePart,
  index,
  onSelect,
  renderPart,
  isOverlay = false,
  readOnly = false,
}: {
  scanTemplatePart: T;
  index: number;
  onSelect: () => void;
  renderPart: (scanTemplatePart: T) => React.ReactNode;
  isOverlay?: boolean;
  readOnly?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: scanTemplatePart.id, disabled: isOverlay || readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  if (isOverlay) {
    return (
      <div css={DRAG_OVERLAY_ITEM_STYLE}>
        <div css={LINE_ITEM_COUNT_STYLE}>{index + 1}.</div>
        <div css={LINE_ITEM_CONTENT_WRAPPER_STYLE}>
          <span css={LINE_ITEM_TEXT_WRAPPER_STYLE}>
            {renderPart(scanTemplatePart)}
          </span>
        </div>
        <div css={DRAG_HANDLE_STYLE}>
          <DragIndicatorIcon fontSize='small' />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      css={[
        LINE_ITEM_ITEM_STYLE,
        isDragging && LINE_ITEM_ITEM_DRAGGING_STYLE,
      ]}
      {...(readOnly ? {} : { ...attributes, ...listeners })}
      onClick={onSelect}
    >
      <div css={LINE_ITEM_COUNT_STYLE}>{index + 1}.</div>
      <div css={LINE_ITEM_CONTENT_WRAPPER_STYLE}>
        <span css={LINE_ITEM_TEXT_WRAPPER_STYLE}>
          {renderPart(scanTemplatePart)}
        </span>
      </div>
      {!readOnly && (
        <div css={DRAG_HANDLE_STYLE}>
          <DragIndicatorIcon fontSize='small' />
        </div>
      )}
    </div>
  );
};

export const OrderableScanTemplatePartList = <T extends ScanTemplatePart>({
  scanTemplateParts,
  onReorder,
  onSelectPart,
  renderPart,
  readOnly,
}: {
  scanTemplateParts: T[];
  onReorder: (reorderedParts: T[]) => void;
  onSelectPart: (scanTemplatePart: T) => void;
  renderPart: (scanTemplatePart: T) => React.ReactNode;
  readOnly?: boolean;
}) => {
  const [draggingScanTemplatePartId, setDraggingScanTemplatePartId] = useState<string | null>(null);
  const [orderedScanTemplatePartIds, setOrderedScanTemplatePartIds] = useState<string[]>(() => scanTemplateParts.map(li => li.id));

  const partIds = scanTemplateParts.map(li => li.id);
  const partIdsKey = partIds.join(',');
  // partIdsKey used intentionally: partIds is a new array every render and would cause infinite loop
  useEffect(() => {
    setOrderedScanTemplatePartIds(partIds);
  }, [partIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 8 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 250, tolerance: 8 },
  });
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggingScanTemplatePartId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = orderedScanTemplatePartIds.indexOf(active.id as string);
    const newIndex = orderedScanTemplatePartIds.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      const newOrderedIds = arrayMove(orderedScanTemplatePartIds, oldIndex, newIndex);
      setOrderedScanTemplatePartIds(newOrderedIds);

      const reorderedScanTemplateParts = newOrderedIds
        .map(id => scanTemplateParts.find(li => li.id === id))
        .filter((li): li is T => !!li);

      onReorder(reorderedScanTemplateParts);
    }
  };

  const draggingScanTemplatePart = draggingScanTemplatePartId ? scanTemplateParts.find(li => li.id === draggingScanTemplatePartId) : null;

  const modifiers = [
    ({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) => ({
      ...transform,
      x: transform.x + 20, // Offset 20px to the right of cursor
    }),
  ];

  return (
    <div css={LINE_ITEM_LIST_STYLE}>
      <DndContext
        sensors={sensors}
        onDragStart={event => setDraggingScanTemplatePartId(event.active.id as string)}
        onDragEnd={handleDragEnd}
        autoScroll={false}
      >
        <SortableContext items={orderedScanTemplatePartIds} strategy={verticalListSortingStrategy}>
          {orderedScanTemplatePartIds.map((scanTemplatePartId, index) => {
            const scanTemplatePart = scanTemplateParts.find(li => li.id === scanTemplatePartId);
            if (!scanTemplatePart) return null;

            return (
              <ScanTemplatePartListItem
                key={scanTemplatePart.id}
                scanTemplatePart={scanTemplatePart}
                index={index}
                onSelect={() => onSelectPart(scanTemplatePart)}
                renderPart={renderPart}
                readOnly={readOnly}
              />
            );
          })}
        </SortableContext>
        {draggingScanTemplatePart && createPortal(
          <DragOverlay dropAnimation={null} modifiers={modifiers}>
            <ScanTemplatePartListItem
              scanTemplatePart={draggingScanTemplatePart}
              index={orderedScanTemplatePartIds.indexOf(draggingScanTemplatePart.id)}
              onSelect={() => {}}
              renderPart={renderPart}
              isOverlay={true}
            />
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </div>
  );
};
